/**
 * Signed-in preview mode — a DEVELOPMENT-ONLY door for screenshotting the
 * gated surfaces (Home, Career DNA™, Journey, Learning) without a session.
 *
 * Rules, deliberately strict:
 *  - It is inert in any production build (`import.meta.env.DEV` gate). The
 *    published app can never be viewed through this door.
 *  - It grants no data access. There is no token, no RLS bypass; the only
 *    thing it does is let the router render the page and hand the page a
 *    clearly-labelled demo payload.
 *
 * Turn it on with `?preview=1`, off with `?preview=0`. The flag lives in
 * sessionStorage so navigation inside the shell keeps it.
 */

const KEY = "wcc360.preview";

export function isPreviewMode(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === "undefined") return false;
  try {
    const p = new URLSearchParams(window.location.search).get("preview");
    if (p === "1") sessionStorage.setItem(KEY, "1");
    if (p === "0") sessionStorage.removeItem(KEY);
    return sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function setPreviewMode(on: boolean): void {
  if (!import.meta.env.DEV) return;
  try {
    if (on) sessionStorage.setItem(KEY, "1");
    else sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable — nothing to do */
  }
}

/** Obviously-fake learner so a screenshot can never be mistaken for real data. */
export const PREVIEW_LEARNER = {
  id: "preview-learner",
  name: "Preview Learner",
  archetypePrimary: "Builder",
  archetypeSecondary: "Connector",
  ageGroup: "15-18",
} as const;

/**
 * A minimal, valid student payload shaped exactly like the sealed
 * Experience Engine™ object. Used ONLY in preview mode.
 */
export function previewStudentPayload(): Record<string, unknown> {
  const intel = (code: string, name: string, value: number, explanation: string) => ({
    intelligence_code: code,
    intelligence_name: name,
    value,
    confidence: 0.82,
    is_null: false,
    computation_hash: "preview",
    explainability: { value, explanation, evidence: ["preview"], confidence: 0.82 },
  });
  const feature = (key: string, label: string, family: string) => ({
    feature_key: key,
    feature_label: label,
    feature_family: family,
    presentation: true,
    value: 62,
    confidence: 0.7,
    is_null: false,
    computation_hash: "preview",
  });
  const rec = (rank: number, id: string, nameOf: string, cluster: string, score: number) => ({
    recommendation_rank: rank,
    career_id: id,
    career_name: nameOf,
    career_cluster: cluster,
    recommendation_score: score,
    fit_score: score,
    confidence: 0.78,
    tier: rank === 1 ? "best" : rank < 4 ? "good" : "explore",
    explanation: `Preview data — ${nameOf} lines up with how you like to work.`,
    matching_intelligences: ["strength", "interest"],
    matching_features: ["hands_on"],
    computation_hash: "preview",
  });

  const top = [
    intel("purpose", "Purpose", 78, "Preview data — you keep choosing work that helps someone concrete."),
    intel("strength", "Signature strength", 84, "Preview data — you make things work with your hands and your head."),
    intel("interest", "Interest theme", 71, "Preview data — systems, tools and how things fit together."),
  ];

  return {
    audience: "student",
    status: { status: "ready" },
    learner: {
      learner_id: PREVIEW_LEARNER.id,
      display_name: PREVIEW_LEARNER.name,
      age_group: PREVIEW_LEARNER.ageGroup,
    },
    identity: {
      archetype_primary: PREVIEW_LEARNER.archetypePrimary,
      archetype_secondary: PREVIEW_LEARNER.archetypeSecondary,
    },
    career_dna: { top_intelligences: top, all: top },
    hdi_summary: top,
    career_matches: [],
    recommendations: [
      rec(1, "prev-1", "Mechatronics Technician", "Engineering & Making", 88),
      rec(2, "prev-2", "Civil Site Engineer", "Built Environment", 81),
      rec(3, "prev-3", "Renewable Energy Installer", "Energy", 76),
      rec(4, "prev-4", "Product Designer", "Design", 71),
    ],
    growth_opportunities: [
      feature("public_speaking", "Speaking to a room", "communication"),
      feature("planning", "Planning further ahead", "organisation"),
    ],
    future_skills: [
      feature("systems_thinking", "Systems thinking", "cognitive"),
      feature("tool_fluency", "Tool fluency", "technical"),
    ],
    provenance: {
      engine_version: "preview",
      item_bank_version: "preview",
      feature_registry_version: "preview",
      intelligence_registry_version: "preview",
      career_dataset_version: "preview",
      recommendation_recipe_version: "preview",
      experience_version: "preview",
      experience_template_id: "preview",
      generated_at: new Date().toISOString(),
      canonical: true,
      computation_hashes: {},
    },
    report_hash: "preview-only-not-canonical",
  };
}
